// ABOUTME: Cross-domain comparison sidebar for a clicked pivot row — renders detail.crossDomain (passed in
// ABOUTME: by RhPivotRowDetail from its single pivotdetail query) as title + MiniBox split-boxes. No own request.
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";
import { formatPrice } from "../../../../../utils/helpers";

const round1 = (n) => Math.round(n * 10) / 10;

// home report type -> translation key for the "Vergeleken met …" title.
const HOME_KEY = {
    orders: "orders",
    inbounds: "inbounds",
    returns: "returns",
    shipments: "shipments",
    invoiceinvoiceproducts: "cd_invoiced",
    warehouselocations: "cd_locations",
};

export const RhCrossDomainSidebar = ({ crossDomain }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";

    if (!crossDomain || !crossDomain.boxes?.length) return null;

    const fmt = (value, format) => {
        if (format === "price") return formatPrice(i18n, value);
        if (format === "pct") return `${round1(value)}%`;
        // 'YYYY-MM-DD...' -> 'DD-MM'
        if (format === "date") return value ? `${value.slice(8, 10)}-${value.slice(5, 7)}` : "–";
        return round1(value);
    };

    const cardBg = isDark ? colors.grey[400] : colors.grey[0];
    // Three tile shades + the green accent, matching the sidebar prototype: green-labelled headline cell,
    // darker neutral cells, lightest de-emphasized "Totaal" cell.
    const tintFor = (kind) => {
        if (kind === "green") return colors.green[100];
        if (kind === "red") return colors.red[100];
        if (kind === "total") return isDark ? colors.grey[600] : colors.grey[100];
        return isDark ? colors.grey[300] : colors.grey[50];
    };

    // num on top, label below — mirrors RhPivotRowDetail's MiniBox; first cell green, total de-emphasized.
    const MiniBox = ({ kind, num, label, sub, full }) => (
        <Box
            sx={{
                flex: full ? "1 1 100%" : "1 1 40%",
                minWidth: 0,
                overflow: "hidden",
                borderRadius: 3,
                px: 1.5,
                py: 1.5,
                background: tintFor(kind),
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1,
                    mb: "auto",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 17,
                        fontWeight: 700,
                        lineHeight: "17px",
                        color: colors.txt.primary,
                    }}
                >
                    {num}
                </Typography>
            </Box>
            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',
                }}
            >
                {sub && (
                    <Text bold variant={kind === "green" ? "green" : kind === "red" ? "red" : null} sx={{ fontSize: 14, whiteSpace: "nowrap" }}>
                        {sub}
                    </Text>
                )}
                {kind === "green" || kind === "red" ? (
                    <Text variant={kind}>
                        {label}
                    </Text>
                ) : (
                    <Text light>
                        {label}
                    </Text>
                )}
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                background:theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[0],
                width: 360,
                flexShrink: 0,
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            <Box>
                <Subheading>
                    {`${t("cd_comparison")}`}
                </Subheading>
                <Text light sx={{ fontSize: 13, mt: 0.25 }}>
                    {crossDomain.entity?.name
                        ? `${t("cd_same_period")} · ${crossDomain.entity.name}`
                        : t("cd_same_period")}
                </Text>
            </Box>
            {crossDomain.boxes.map((box, i) => {
                const cells = box.cells || [];
                const inlineTotal = box.total && cells.length === 1;
                const fullTotal = box.total && cells.length !== 1;
                return (
                    <Box
                        key={`${box.domain}-${i}`}
                        sx={{
                            background: cardBg,
                            border: isDark ? "none" : `1px solid ${colors.grey[100]}`,
                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                            borderRadius: 3,
                            p: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <Text bold>{t(box.title)}</Text>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                            {cells.map((c, ci) => (
                                <MiniBox
                                    key={ci}
                                    kind={c.variant || "neutral"}
                                    num={fmt(c.value, c.format)}
                                    label={t(c.label)}
                                    sub={c.sub}
                                />
                            ))}
                            {inlineTotal && (
                                <MiniBox
                                    kind="total"
                                    num={fmt(box.total.value, box.total.format)}
                                    label={t(box.total.label)}
                                />
                            )}
                            {fullTotal && (
                                <MiniBox
                                    kind="total"
                                    full
                                    num={fmt(box.total.value, box.total.format)}
                                    label={t(box.total.label)}
                                />
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
